import React, { memo, useCallback, useEffect, useState } from 'react';

import { FormAutocomplete, FormFieldCurrency, Loader, GlobalInterfaces } from '@monorepo/common';

import SalaryTitle from '../../../../../components/JobStepper/components/SalaryTitle';
import { ISalaryProps } from './salaryInterface';
import { useAppSelector } from '../../../../../store/hooks';
import {
  useGetSalaryTypesQuery,
  useGetSalaryUnitsQuery
} from '../../../../../services/servicePostAJob';

import { useStyles } from '../../../styles';
import { convertNumber } from './convertNumber';
import { RespSalaryTypes } from '../../../../../services/servicePostAJob/responseInterfaces';

interface IOption extends GlobalInterfaces.IOption{
  selected?: boolean,
}

const Salary = ({ getValues, setValue, errors, register, defaultValueRadio, defaultValueExact, defaultValueMin, defaultValueMax, defaultValueSalaryUnitId }:ISalaryProps) => {
  const classes = useStyles();
  const [activeItem, setActiveItem] = useState(Number(defaultValueRadio));
  const { isHost } = useAppSelector(({ pathnameSettings }) => ({
    isHost: pathnameSettings.host
  }));
  const { data: salaryTypes } = useGetSalaryTypesQuery(null);

  const { data: salaryUnits, isLoading: isLoadingSalary } = useGetSalaryUnitsQuery(null, {
    skip: !isHost
  });

  const handleChange = useCallback((selection: RespSalaryTypes | RespSalaryTypes[]) => {
    const num = Number(Array.isArray(selection) ? selection[0].value : selection.value);
    if (num === 3) {
      setValue('salaryUnitId', null);
    } else {
      setValue('salaryUnitId', defaultValueSalaryUnitId);
    }
    setActiveItem(num);
    setValue('salaryUnitRadio', num);
    setValue('salaryExact', null);
    setValue('salaryMin', null);
    setValue('salaryMax', null);
  }, [setValue]);

  useEffect(() => {
    setValue('salaryUnitRadio', defaultValueRadio);
  }, [defaultValueRadio, setValue]);

  useEffect(() => {
    if (salaryUnits) {
      const filtered = salaryUnits.filter((item) => !!item.selected);
      if (filtered.length === 1 && !getValues('salaryUnitId') && Number(getValues('salaryUnitRadio')) !== 3) {
        setValue('salaryUnitId', filtered[0].value);
      }
    }
  }, [salaryUnits, getValues, setValue]);

  useEffect(() => {
    if (activeItem === 1) {
      setValue('showSalaryExact', true);
      return;
    }
    setValue('showSalaryExact', false);
  }, [activeItem]);

  if (!isLoadingSalary) {
    return (
      <>
        <input
          style={{ display: 'none' }}
          type="checkbox"
          {...register('showSalaryExact')}
        />
        <SalaryTitle />

        {salaryTypes && (
          <FormAutocomplete<RespSalaryTypes>
            optionsSettings={{
              optionValue: 'value',
              optionText: 'label'
            }}
            isRequired
            disableClearable
            extraProps={{ ...register('salaryUnitRadio') }}
            errors={errors}
            getValues={getValues}
            options={salaryTypes}
            label="Select Compensation Option"
            setValue={setValue}
            onChange={handleChange}
          />
        )}

        {activeItem !== 3 && (
        <FormAutocomplete<IOption>
          optionsSettings={{
            optionValue: 'value',
            optionText: 'text'
          }}
          isRequired
          disableClearable
          extraProps={{ ...register('salaryUnitId') }}
          errors={errors}
          getValues={getValues}
          options={salaryUnits}
          label="Specify Compensation Per"
          setValue={setValue}
        />
        )}

        {activeItem === 1 && (
        <FormFieldCurrency
          isRequired
          name="salaryExact"
          change={(a) => {
            setValue('salaryExact', convertNumber(a));
          }}
          dataCy="salaryExact"
          errors={errors}
          valueField={defaultValueExact}
          label="Exact Compensation (in dollars)"
        />
        )}
        {activeItem === 2 && (
        <div className={classes.couple_line}>
          <FormFieldCurrency
            isRequired
            name="salaryMin"
            change={(a) => {
              setValue('salaryMin', convertNumber(a));
            }}
            errors={errors}
            valueField={defaultValueMin}
            fieldComment="Range (in dollars)"
            dataCy="salaryMin"
          />
          <FormFieldCurrency
            isRequired
            name="salaryMax"
            change={(a) => {
              setValue('salaryMax', convertNumber(a));
            }}
            dataCy="salaryMax"
            errors={errors}
            valueField={defaultValueMax}
          />
        </div>
        )}
      </>
    );
  }
  return (
    <Loader isShow />
  );
};

export default memo(Salary);
